<script setup>
import DropdownComponent from '@/components/DropdownComponent.vue';
import { useClickAway } from '@/utils/useClickAway';
import { useSlots, ref, computed, watch } from 'vue';

const props = defineProps({
  wrapperClass: {
    type: String,
    required: false,
    default: '',
  },
  inputType: {
    type: String,
    required: false,
    default: 'text',
  },
  placeholder: {
    type: String,
    required: false,
    default: '',
  },
  isDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideInput: {
    type: Boolean,
    required: false,
    default: false,
  },
  showClearButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  dropdownList: {
    type: Array,
    required: false,
    default: () => [],
  },
  canShowDropdown: {
    type: Boolean,
    required: false,
    default: true,
  },
  inputLabel: {
    type: String,
    required: false,
    default: '',
  },
  hasBadge: {
    type: Boolean,
    required: false,
    default: false,
  },
  badgeText: {
    type: String,
    required: false,
    default: '',
  },
  hasCaretDown: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasAutoComplete: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasError: {
    type: Boolean,
    required: false,
    default: false,
  },
  defaultInputValue: {
    type: String,
    required: false,
    default: '',
  },
  value: {
    type: String,
    required: false,
    default: '',
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
});

// watch(
//   () => dropdownList,
//   (newValue) => {
//     console.log('Dropdown list updated:', newValue);
//   },
//   { immediate: true }
// );
const showDropdown = ref(false);
// const { shouldHideItems, setShouldHideItems } = useHideToggle();
// const shouldHideDropdown = computed(() => shouldHideItems.value[inputLabel]);
const inputValue = ref(props.defaultInputValue);
// canShowDropdown is a prop that determines if the dropdown should be shown based on additional conditions
const isDropdownVisible = computed(
  () => showDropdown.value && props.canShowDropdown
);

const inputWrapperRef = ref(null);
useClickAway(inputWrapperRef, () => {
  showDropdown.value = false;
});

watch(
  () => props.value,
  (newValue) => {
    console.log('input value update', newValue);
    inputValue.value = newValue;
  }
);
// watch(
//   () => isDropdownVisible.value,
//   (newValue) => {
//     console.log('Dropdown visibility changed:', newValue);
//   },
//   { immediate: true }
// );
const emit = defineEmits([
  'wasClicked',
  'isDirty',
  'enterPress',
  'hoverOn',
  'hoverOff',
  'inputChange',
  'inputClick',
  'onBlur',
  'onFocus',
  'dropdownClick',
  'badgeClick',
]);
const handleInputChange = (event) => {
  inputValue.value = event.target.value;
  if (!inputValue.value.length) {
    showDropdown.value = false;
  } else {
    showDropdown.value = true;
  }
  emit('inputChange', event.target.value, props.inputLabel);
};
const handleInputClick = (event) => {
  isFocused.value = true;
  // console.log('Input clicked:', inputValue.value);
  if (!inputValue.value.length) {
    showDropdown.value = false;
  } else {
    showDropdown.value = true;
  }
  // console.log('showDropdown', showDropdown.value);
  emit('inputClick', event.target.value, props.inputLabel);
};
const handleClearButtonClick = () => {
  inputValue.value = '';
  emit('inputChange', inputValue.value, props.inputLabel);
};
const isFocused = ref(false);
const handleFocus = (event) => {
  isFocused.value = true;
  //   setShouldHideItems({
  //     ...shouldHideItems.value,
  //     [inputLabel]: false,
  //   });
  //   console.log('dropdowns', shouldHideItems.value);
  emit('onFocus', event.target.value, props.inputLabel);
};
const handleBlur = (event) => {
  isFocused.value = false;
  emit('onBlur', event.target.value, props.inputLabel);
};
const handleBadgeClick = () => {
  emit('badgeClick', props.inputLabel);
};
const slots = useSlots();
// const handleDropdownClick = (item) => {
//   showDropdown.value = !showDropdown.value;
//   if (!hasBadge) {
//     inputValue.value = item.label || item.id || '';
//   }
//   //   setShouldHideItems({
//   //     ...shouldHideItems.value,
//   //     [inputLabel]: true,
//   //   });
//   emit('dropdownClick', inputLabel, item);
// };
const toggleDropdown = () => {
  // console.log('toggleDropdown called');
  showDropdown.value = !showDropdown.value;
};

const handleDropdownClick = (item) => {
  showDropdown.value = false;
  emit('dropdownClick', props.inputLabel, item);
};
</script>

<template>
  <div
    ref="inputWrapperRef"
    class="default-input-wrapper relative rounded-lg"
    :class="[
      props.wrapperClass,
      hasError && !isFocused
        ? 'outline-alert-border bg-steel-100 outline outline-1 outline-offset-[-1px]'
        : isFocused
          ? 'outline-neutral-ring bg-white outline outline-1 outline-offset-[-1px]'
          : 'default-input-wrapper-border hover:bg-info-subtle-bg bg-steel-100',
    ]"
  >
    <div
      v-if="slots.leftIcon"
      class="h-6 w-6"
    >
      <slot name="leftIcon"></slot>
    </div>
    <div
      v-if="hideInput"
      id="origin-badge"
      class="flex items-center justify-center gap-2 rounded-full bg-dark-gray px-2.5 py-0.5 text-base text-white"
    >
      <div class="whitespace-nowrap text-sm">{{ badgeText }}</div>
      <div
        @click.stop="handleBadgeClick"
        class="h-4 w-4 cursor-pointer hover:opacity-80"
      >
        <svg
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </div>
    </div>
    <input
      v-else
      :id="inputLabel"
      class="custom-input"
      :type="inputType"
      :placeholder="placeholder"
      :value="inputValue"
      @input="handleInputChange"
      @click="handleInputClick"
      @mouseover="emit('hoverOn')"
      @mouseleave="emit('hoverOff')"
      @keydown="emit('isDirty')"
      @keyup.enter="emit('enterPress')"
      @blur="handleBlur"
      @focus="handleFocus"
      :disabled="isDisabled"
      :readonly="readonly"
      :autocomplete="hasAutoComplete ? 'on' : 'off'"
      :aria-label="inputLabel"
      :aria-labelledby="inputLabel"
      :aria-describedby="inputLabel"
    />
    <div
      v-if="!hideInput && showClearButton && inputValue?.length > 0"
      @click="handleClearButtonClick"
      class="active:text-selected-tab-text absolute right-2 h-5 w-5 hover:cursor-pointer hover:text-neutral-blue"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M18.3639 7.05026L16.9497 5.63605L11.9999 10.5858L7.0502 5.63605L5.63599 7.05026L10.5857 12L5.63599 16.9498L7.0502 18.364L11.9999 13.4142L16.9497 18.364L18.3639 16.9498L13.4142 12L18.3639 7.05026Z"
          fill="#374053"
        />
      </svg>
    </div>
    <div
      v-if="slots.rightIcon"
      class="h-6 w-6"
    >
      <slot name="rightIcon"></slot>
    </div>
    <button
      type="button"
      @click.stop="toggleDropdown"
      v-if="hasCaretDown"
      class="h-6 w-6"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.85865 10.2729C5.66065 10.0749 5.56164 9.97585 5.52455 9.86169C5.49192 9.76127 5.49192 9.6531 5.52455 9.55267C5.56164 9.43851 5.66065 9.33951 5.85865 9.1415L6.1415 8.85865C6.3395 8.66065 6.43851 8.56164 6.55267 8.52455C6.6531 8.49192 6.76127 8.49192 6.86169 8.52455C6.97585 8.56164 7.07486 8.66065 7.27287 8.85865L11.4344 13.0202C11.6324 13.2182 11.7314 13.3172 11.8456 13.3543C11.946 13.3869 12.0542 13.3869 12.1546 13.3543C12.2687 13.3172 12.3678 13.2182 12.5658 13.0202L16.7273 8.85865C16.9253 8.66065 17.0243 8.56164 17.1385 8.52455C17.2389 8.49192 17.3471 8.49192 17.4475 8.52455C17.5616 8.56164 17.6606 8.66065 17.8587 8.85865L18.1415 9.1415C18.3395 9.33951 18.4385 9.43851 18.4756 9.55267C18.5082 9.6531 18.5082 9.76127 18.4756 9.86169C18.4385 9.97585 18.3395 10.0749 18.1415 10.2729L12.5658 15.8486C12.3678 16.0466 12.2687 16.1456 12.1546 16.1827C12.0542 16.2153 11.946 16.2153 11.8456 16.1827C11.7314 16.1456 11.6324 16.0466 11.4344 15.8486L5.85865 10.2729Z"
          fill="#6C7C9D"
        />
      </svg>
    </button>
    <DropdownComponent
      @dropdown-click="handleDropdownClick"
      :dropdown-list="dropdownList"
      :is-dropdown-visible="isDropdownVisible && dropdownList.length > 0"
      :source="inputLabel"
    />
  </div>
</template>

<style scoped>
.custom-input {
  outline: none;
  border: none;
  padding: 0;
  border-radius: 0;
  background-image: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  flex-grow: 1;
  line-height: 1.25;
  letter-spacing: -0.025em;
  font-weight: 400;
  color: #374053;
}
.custom-input::placeholder {
  color: rgba(92, 107, 138, 1);
  line-height: 1.25;
  letter-spacing: -0.025em;
  font-weight: 400;
}

.default-input-wrapper-border {
  outline: 1px solid rgba(31, 36, 46, 0.14);
}
</style>
